import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, NgModule, Output, QueryList, TemplateRef, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { AppLayoutModule } from '../../modules/app-layout/app-layout.module';
import { SaraPlusFormModule } from '@sp-forms/saraplus-ngx-forms';
import { AppBaseModule } from '../../modules/app-base.module';
import { NotificationService } from '../../services/notification.service';
import { EnvironmentService } from '../../services/environment.service';
import { OtpVerificationModalComponent, OtpVerificationModalModule } from '../otp-verification-modal/opt-verification-modal.component';
import { DataService } from '../../services/data.service';
import { StateService } from '../../services/state.service';
import { take } from 'rxjs';
import { TranslationModule } from '../translation.module';

export enum PhoneVerifyStatus {
  VerifiedByCustomer,
  Verified,
  Invalid,
  InProgress
}
@Component({
    selector: 'app-phone-verification',
    templateUrl: './phone-verification.component.html',
    styleUrl: './phone-verification.component.scss',
    standalone: false
})
export class PhoneVerificationComponent implements AfterContentInit {

  @Input() formGroupRef: FormGroup | undefined;
  @Input() controlName: string | undefined;
  @Input() control: AbstractControl | undefined;
  @Input() styleClass: string = '';
  @Input() iconStyleClass: string = '';
  @Input() showSearchIcon = true
  @Input() showLabel = true; 
  @Input() hideValidatePhone = true
  @Output() OnSearchClicked: EventEmitter<Event> = new EventEmitter();
  @Input() isAlreadyVerifiedByCustomer = false;
  @Output() verificationStatus: EventEmitter<PhoneVerifyStatus> = new EventEmitter();
  @Output() ChangeInputFocus: EventEmitter<void> = new EventEmitter();
  @Output() ChangeInputBlur: EventEmitter<void> = new EventEmitter();

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined;
  @ViewChild('optVerificationModal') optVerificationModal?: OtpVerificationModalComponent

  #dataService = inject(DataService)
  #environmentService = inject(EnvironmentService)
  #stateService = inject(StateService)
  #notificationService = inject(NotificationService)

  labelTemplate: TemplateRef<any> | undefined;
  controlTemplate: TemplateRef<any> | undefined;
  status: PhoneVerifyStatus = PhoneVerifyStatus.Invalid;
  lastVerifiedValue?: string;
  showOtpDialog = false;



  ngAfterContentInit(): void {
    this.mapTemplates();
    this.registerInputChanges();
    if (this.isAlreadyVerifiedByCustomer) this.status = PhoneVerifyStatus.VerifiedByCustomer;
    else this.status = PhoneVerifyStatus.Verified;
  }

  mapTemplates() {
    if (this.templates) {
      (this.templates as QueryList<PrimeTemplate>).forEach(item => {
        switch (item.getType()) {
          case 'label':
            this.labelTemplate = item.template;
            break;
          case 'control':
            this.controlTemplate = item.template;
            break;
        }
      });
    }
  }

  formatPhoneNumber(event: any) {
    const input = event.target.value.replace(/\D/g, '').substring(0, 10); // Remove non-digits and limit to 10 digits
    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6);

    if (input.length > 6) {
      this.controlName = `${areaCode}-${middle}-${last}`;
    } else if (input.length > 3) {
      this.controlName = `${areaCode}-${middle}`;
    } else if (input.length > 0) {
      this.controlName = `${areaCode}`;
    } else {
      this.controlName = '';
    }

    // Set the formatted value back to the input
    event.target.value = this.controlName;
    this.control?.setValue(this.controlName);
    this.control?.updateValueAndValidity();
    this.updateStatus();
  }

  registerInputChanges() {
    if (this.control) {
      this.control.valueChanges.subscribe({
        next: () => {
          if (!this.control?.valid) {
            this.status = PhoneVerifyStatus.Invalid;
          }
        }
      })
    }
  }

  onInputBlur() {
    this.updateStatus();
  }

  verifyPhone() {
    this.status = PhoneVerifyStatus.Verified;
    this.verificationStatus.emit(this.status);
  }

  get statusIcon() {
    let icon = 'pi-phone-red-sara';
    switch (this.status) {
      case PhoneVerifyStatus.Invalid:
        icon = 'pi-phone-red-sara'
        break;
      case PhoneVerifyStatus.VerifiedByCustomer:
        icon = 'pi-phone-green-sara-verified'
        break;
      case PhoneVerifyStatus.Verified:
        icon = 'pi-phone-green-sara'
        break;
      case PhoneVerifyStatus.InProgress:
        icon = 'pi-spinner pi-spin'
        break;
    }
    return icon;
  }

  get isInProgress() {
    return this.status === PhoneVerifyStatus.InProgress;
  }

  validate() {
    if (this.control?.valid) {
      const endpoint = this.#environmentService.getUrl('securedata/validatecontact');
      const body = {
        UniqueId: this.#stateService.ConnectionId,
        MobilePhone: this.control?.value
      }
      this.#dataService.post<unknown, void>(endpoint, body).pipe(take(1)).subscribe({
        next: () => {
          this.#notificationService.showInfo("OTP sent successfully.", "Information");
          this.showOtpDialog = true;
        }
      })
    }
  }

  updateStatus() {
    if (!this.control?.valid) {
      this.status = PhoneVerifyStatus.Invalid
    }
    else if (this.status !== PhoneVerifyStatus.InProgress && !this.isLastVerifiedValue) {
      this.verifyPhone();
    } else if (this.isLastVerifiedValue) {
      this.status = PhoneVerifyStatus.VerifiedByCustomer;
    }
  }

  get isLastVerifiedValue() {
    return this.control?.value && this.lastVerifiedValue?.toLowerCase() === (this.control.value as string).toLowerCase();
  }

  onPinValidate(pin?: string) {
    if (pin) {
      const endpoint = this.#environmentService.getUrl('securedata/validatecontactpin');
      const body = {
        UniqueId: this.#stateService.ConnectionId,
        MobilePhone: this.control?.value,
        Pin: +pin
      }
      this.#dataService.post<unknown, void>(endpoint, body, undefined, true).pipe(take(1)).subscribe({
        next: () => {
          this.#notificationService.showInfo("OTP verified.", "Information");
          this.optVerificationModal?.onPinValidated(true);
          this.status = PhoneVerifyStatus.VerifiedByCustomer;
          this.lastVerifiedValue = this.control?.value;
          this.verificationStatus.emit(this.status);
        },
        error: (err) => {
          if (err) {
            this.optVerificationModal?.onPinValidated(false, err.error.ErrorDescription);
          }
        }
      })
    }
  }

  onInputFocus() {
    this.ChangeInputFocus.emit();
  }
}

@NgModule({
  declarations: [
    PhoneVerificationComponent
  ],
  imports: [
    AppBaseModule,
    SaraPlusFormModule,
    AppLayoutModule,
    OtpVerificationModalModule,
    TranslationModule
  ],
  exports: [
    PhoneVerificationComponent
  ],
  providers: [
    EnvironmentService
  ]
})
export class PhoneVerificationModule {

}
